import React, { useState } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CForm,
  CFormInput,
  CButton,
  CAlert,
  CSpinner,
  CInputGroup
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilLockLocked, cilCheckCircle, cilWarning } from '@coreui/icons';
import { authAPI } from '../../services/api';
import SecureCaptcha from '../SecureCaptcha';

const ChangePasswordModal = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    isValid: false,
    checks: {
      length: false,
      uppercase: false,
      lowercase: false,
      digit: false,
      special: false
    }
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear errors when user starts typing
    if (error) setError('');
    if (success) setSuccess('');

    // Validate password strength in real-time for new password
    if (name === 'new_password') {
      validatePasswordStrength(value);
    }
  };

  const validatePasswordStrength = (password) => {
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      digit: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)
    };

    const isValid = Object.values(checks).every(Boolean);

    setPasswordStrength({
      isValid,
      checks
    });
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Basic validation
    if (!formData.email || !formData.current_password || !formData.new_password || !formData.confirm_password) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (!passwordStrength.isValid) {
      setError('Please ensure your new password meets all requirements');
      return;
    }

    // Show captcha for security
    setShowCaptcha(true);
  };

  const handleCaptchaSuccess = async () => {
    setShowCaptcha(false);
    setLoading(true);

    try {
      const response = await authAPI.changePassword(formData);
      
      if (response.data.success) {
        setSuccess('Password changed successfully!');
        setFormData({
          email: '',
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
        setPasswordStrength({
          isValid: false,
          checks: {
            length: false,
            uppercase: false,
            lowercase: false,
            digit: false,
            special: false
          }
        });
        setShowPasswords({
          current: false,
          new: false,
          confirm: false
        });
        
        // Auto close after success
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCaptchaClose = () => {
    setShowCaptcha(false);
  };

  const handleClose = () => {
    setFormData({
      email: '',
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setError('');
    setSuccess('');
    setPasswordStrength({
      isValid: false,
      checks: {
        length: false,
        uppercase: false,
        lowercase: false,
        digit: false,
        special: false
      }
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    onClose();
  };

  return (
    <div>
      <CModal visible={show} onClose={handleClose} size="lg">
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilLockLocked} className="me-2" />
            Change Password
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {/* Success Alert */}
          {success && (
            <CAlert color="success" className="mb-3">
              <CIcon icon={cilCheckCircle} className="me-2" />
              {success}
            </CAlert>
          )}
          
          {/* Error Alert */}
          {error && (
            <CAlert color="danger" className="mb-3">
              {error}
            </CAlert>
          )}
          
          <CForm onSubmit={handleSubmit}>
            <div className="mb-3">
              <CFormInput
                type="email"
                name="email"
                placeholder="Your Email Address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <CInputGroup>
                <CFormInput
                  type={showPasswords.current ? "text" : "password"}
                  name="current_password"
                  placeholder="Current Password"
                  value={formData.current_password}
                  onChange={handleChange}
                  required
                />
                <CButton
                  type="button"
                  variant="outline"
                  onClick={() => togglePasswordVisibility('current')}
                  style={{ minWidth: '80px' }}
                >
                  {showPasswords.current ? 'Hide' : 'Show'}
                </CButton>
              </CInputGroup>
            </div>
            
            <div className="mb-3">
              <CInputGroup>
                <CFormInput
                  type={showPasswords.new ? "text" : "password"}
                  name="new_password"
                  placeholder="New Password"
                  value={formData.new_password}
                  onChange={handleChange}
                  required
                />
                <CButton
                  type="button"
                  variant="outline"
                  onClick={() => togglePasswordVisibility('new')}
                  style={{ minWidth: '80px' }}
                >
                  {showPasswords.new ? 'Hide' : 'Show'}
                </CButton>
              </CInputGroup>
              
              {/* Password Strength Indicator */}
              {formData.new_password && (
                <div className="mt-2">
                  <small className="text-medium-emphasis">Password must contain:</small>
                  <div className="mt-1">
                    <div className={`d-flex align-items-center mb-1 ${passwordStrength.checks.length ? 'text-success' : 'text-danger'}`}>
                      <CIcon icon={passwordStrength.checks.length ? cilCheckCircle : cilWarning} className="me-1" size="sm" />
                      <small>At least 8 characters</small>
                    </div>
                    <div className={`d-flex align-items-center mb-1 ${passwordStrength.checks.uppercase ? 'text-success' : 'text-danger'}`}>
                      <CIcon icon={passwordStrength.checks.uppercase ? cilCheckCircle : cilWarning} className="me-1" size="sm" />
                      <small>One uppercase letter</small>
                    </div>
                    <div className={`d-flex align-items-center mb-1 ${passwordStrength.checks.lowercase ? 'text-success' : 'text-danger'}`}>
                      <CIcon icon={passwordStrength.checks.lowercase ? cilCheckCircle : cilWarning} className="me-1" size="sm" />
                      <small>One lowercase letter</small>
                    </div>
                    <div className={`d-flex align-items-center mb-1 ${passwordStrength.checks.digit ? 'text-success' : 'text-danger'}`}>
                      <CIcon icon={passwordStrength.checks.digit ? cilCheckCircle : cilWarning} className="me-1" size="sm" />
                      <small>One digit</small>
                    </div>
                    <div className={`d-flex align-items-center mb-1 ${passwordStrength.checks.special ? 'text-success' : 'text-danger'}`}>
                      <CIcon icon={passwordStrength.checks.special ? cilCheckCircle : cilWarning} className="me-1" size="sm" />
                      <small>One special character (e.g., !@#$%^&*)</small>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mb-4">
              <CInputGroup>
                <CFormInput
                  type={showPasswords.confirm ? "text" : "password"}
                  name="confirm_password"
                  placeholder="Confirm New Password"
                  value={formData.confirm_password}
                  onChange={handleChange}
                  required
                />
                <CButton
                  type="button"
                  variant="outline"
                  onClick={() => togglePasswordVisibility('confirm')}
                  style={{ minWidth: '80px' }}
                >
                  {showPasswords.confirm ? 'Hide' : 'Show'}
                </CButton>
              </CInputGroup>
              
              {/* Password Match Indicator */}
              {formData.confirm_password && formData.new_password && (
                <div className="mt-1">
                  <div className={`d-flex align-items-center ${formData.new_password === formData.confirm_password ? 'text-success' : 'text-danger'}`}>
                    <CIcon icon={formData.new_password === formData.confirm_password ? cilCheckCircle : cilWarning} className="me-1" size="sm" />
                    <small>Passwords {formData.new_password === formData.confirm_password ? 'match' : 'do not match'}</small>
                  </div>
                </div>
              )}
            </div>
            
            <CButton 
              type="submit" 
              color="primary" 
              className="w-100 py-2"
              disabled={loading || !passwordStrength.isValid || formData.new_password !== formData.confirm_password || !formData.email}
            >
              {loading ? (
                <div>
                  <CSpinner size="sm" className="me-2" />
                  Changing Password...
                </div>
              ) : (
                <div>
                  <CIcon icon={cilLockLocked} className="me-2" />
                  Change Password
                </div>
              )}
            </CButton>
          </CForm>
        </CModalBody>
        <CModalFooter className="justify-content-center">
          <small className="text-medium-emphasis">
            For security reasons, please complete the captcha verification to change your password.
          </small>
        </CModalFooter>
      </CModal>

      {/* Secure Captcha Modal for Password Change */}
      <SecureCaptcha
        show={showCaptcha}
        onSuccess={handleCaptchaSuccess}
        onClose={handleCaptchaClose}
        title="Security Verification"
        description="Please complete the security check to change your password."
      />
    </div>
  );
};

export default ChangePasswordModal;